import { memo, useState } from 'react';
import ChevronIcon from 'assets/button-chevron-left.svg?react';
import AuthForm from 'components/auth/tools/AuthForm';
import NiceModal from 'components/modals/NiceModal';
import { getPaymentSession } from 'helpers/trustpayService';
import useModalState from 'hooks/useModalState';
import { useMappedState } from 'redux-react-hook';
import styled, { css, keyframes } from 'styled-components';
import  Spinner  from 'ui-kit/Spinner';
import { isBlank } from 'utils/storeUtils';

type OfferType = 'yearDiscountOffer' | 'monthDiscountOffer' | 'month14daysTrialOffer';

const PaymentActionsSection: React.FC = () => {

  const [isProcessing, setIsProcessing] = useState<Record<OfferType, boolean>>({
    yearDiscountOffer: false,
    monthDiscountOffer: false,
    month14daysTrialOffer: false
  });

  const [isAuthModalOpen, openAuthModal, closeAuthModal] = useModalState();

  const { currentUser } = useMappedState(
    state => {
      const currentUser = state.users.usersMap.get(state.users.currentUserId);
      const hasSubscription = currentUser?.hasSubscription;
      return {
        currentUser: state.users.usersMap.get(state.users.currentUserId),
        hasSubscription: hasSubscription
      };
    }
  );

  const handleGetPaymentSession = async (offerType: OfferType) => {
    if (isProcessing[offerType]) return;

    if (isBlank(currentUser)) {
      openAuthModal();
      return;
    }
    try {
      setIsProcessing(prev => ({ ...prev, [offerType]: true }));
      await getPaymentSession(offerType);
    } catch (error) {
      setIsProcessing({
        yearDiscountOffer: false,
        monthDiscountOffer: false,
        month14daysTrialOffer: false
      });
      throw error;
    }
  };


  return (
    <>
      <Root>
        <PlanButton
          type="button"
          primary
          onClick={() => handleGetPaymentSession('yearDiscountOffer')}
          disabled={isProcessing['yearDiscountOffer']}
        >
          <ButtonContainer>
            <TextColumn>
              <MainText>Buy for a year</MainText>
              <SubText><StrikethroughSubText>$89.99</StrikethroughSubText>$44.99 ($3.75/month)</SubText>
            </TextColumn>
            <RightContainer>
              <RightBox isPrimary>
                <RightText isPrimary>Save 50%</RightText>
              </RightBox>
              {isProcessing['yearDiscountOffer'] ? (
                <Spinner size={20} />
              ) : (
                <IconContainer>
                  <ChevronIcon style={{ height: '100%' }} />
                </IconContainer>
              )}
            </RightContainer>
          </ButtonContainer>
        </PlanButton>

        <PlanButton
          type="button"
          onClick={() => handleGetPaymentSession('monthDiscountOffer')}
          disabled={isProcessing['monthDiscountOffer']}
        >
          <ButtonContainer>
            <TextColumn>
              <MainText>Buy for a month</MainText>
              <SubText><StrikethroughSubText>$12.99</StrikethroughSubText>  $2.99 (1st month)</SubText>
            </TextColumn>
            <RightContainer>
              <RightBox >
                <RightText>Save 76%</RightText>
              </RightBox>
              {isProcessing['monthDiscountOffer'] ? (
                <Spinner size={20} />
              ) : (
                <IconContainer>
                  <ChevronIcon style={{ height: '100%' }} />
                </IconContainer>
              )}
            </RightContainer>
          </ButtonContainer>
        </PlanButton>

        <PlanButton
          type="button"
          onClick={() => handleGetPaymentSession('month14daysTrialOffer')}
          disabled={isProcessing['month14daysTrialOffer']}
        >
          <ButtonContainer>
            <TextColumn>
              <MainText>14 Days - Penny Trial</MainText>
              <SubText>$0.01</SubText>
            </TextColumn>
            <RightContainer>
              {isProcessing['month14daysTrialOffer'] ? (
                <Spinner size={20} />
              ) : (
                <IconContainer>
                  <ChevronIcon style={{ height: '100%' }} />
                </IconContainer>
              )}
            </RightContainer>
          </ButtonContainer>
        </PlanButton>
      </Root>
      {isAuthModalOpen && (
        <NiceModal onClose={closeAuthModal}>
          <ModalInnerPlaceholder>
            <AuthForm onClose={closeAuthModal} />
          </ModalInnerPlaceholder>
        </NiceModal>
      )}
    </>
  );
};

export default memo(PaymentActionsSection);

const Root = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const IconContainer = styled.div`
  display: flex;
  align-items: center;
  height: 24px;
`;

const moveAnimation = keyframes`
  0% { left: -200%; }
  100% { left: 200%; }
`;

const PlanButton = styled.button<{ primary?: boolean }>`
  position: relative;
  width: 100%;
  height: 56px;
  padding: 8px 20px 8px 28px;
  border-radius: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  font-family: 'Nunito Sans';
  cursor: pointer;
  color: white;
  overflow: hidden;

  @media (max-width: 375px) {
    height: 48px;
    padding: 8px 16px 8px 20px;
  }

  ${props => props.primary ? css`
    background: linear-gradient(
      90deg,
      rgb(158, 94, 238) 0%,
      rgb(183, 89, 234) 32%,
      rgb(198, 93, 233) 66%,
      rgb(184, 133, 244) 100%
    );
    box-shadow: none;
    border: none;

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: -100%;
      width: 10px;
      height: 100%;
      background: rgba(255, 255, 255, 0.5);
      filter: blur(20px);
      animation: ${moveAnimation} 2s ease infinite;
    }
  ` : css`
    background: #282828;
    border: 1px solid #686868;
    box-shadow: none;
  `}

  &:hover:not(:disabled) {
    opacity: 0.9;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ButtonContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 0 13px 0 8px;
`;

const TextColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const MainText = styled.p`
  font-family: Nunito Sans;
  font-weight: 700;
  font-size: 18px;
  line-height: 24px;

  @media (max-width: 430px) {
    font-size: 14px;
  }

  @media (max-width: 375px) {
    font-size: 13px;
    line-height: 18px;
  }
`;

const SubText = styled.p`
  font-family: Nunito Sans;
  font-weight: 700;
  font-size: 12px;
  line-height: 16px;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;

  @media (max-width: 360px) {
    font-size: 10px;
  }
`;

const StrikethroughSubText = styled(SubText)`
  text-decoration: line-through;
  opacity: 0.6;
`;

const RightContainer = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-content: center;
  column-gap: 18px;
`;

const RightBox = styled.div<{ isPrimary?: boolean }>`
  height: 24px;
  background-color: ${props => props.isPrimary ? '#ffffff' : 'transparent'};
  padding: 6px;
  border-radius: 6px;
  display: flex;
  justify-content: center;
  border: ${props => props.isPrimary ? 'none' : '1px solid #686868'};
`;

const RightText = styled.div<{ isPrimary?: boolean }>`
  font-size: 12px;
  font-weight: 700;
  background: ${props => props.isPrimary ? 'linear-gradient(90deg, #9e5eee 0%, #b759ea 32%, #c65de9 66%, #b885f4 100%)' : 'transparent'};
  -webkit-background-clip: text;
  background-clip: text;
  color: ${props => props.isPrimary ? 'transparent' : '#ffffff'};
  line-height: 12px;
`;

const ModalInnerPlaceholder = styled.div`
  width: 380px;
  padding: 30px;
  box-sizing: border-box;
  @media (max-width: 400px) {
    width: 100%;
  }
`;
